import { useState, useEffect, useCallback } from "react";

// Use our API proxy to avoid CORS issues with TradingView's API
const TRADINGVIEW_SEARCH_API = "/api/tradingview/symbol-search";

/**
 * Token symbol alternatives for searching
 * ETH and WETH are interchangeable for charting purposes
 */
const TOKEN_ALTERNATIVES: Record<string, string[]> = {
  ETH: ["ETH", "WETH"],
  WETH: ["WETH", "ETH"],
};

/**
 * Get all variants of a token symbol to search for
 */
const getTokenVariants = (symbol: string): string[] => {
  const upperSymbol = symbol.toUpperCase();
  return TOKEN_ALTERNATIVES[upperSymbol] || [upperSymbol];
};

/**
 * Generate all search query combinations for base/quote pairs
 * Includes both normal (BASE/QUOTE) and inverse (QUOTE/BASE) pairs
 */
const generateSearchQueries = (base: string, quote: string): { query: string; isInverse: boolean }[] => {
  const baseVariants = getTokenVariants(base);
  const quoteVariants = getTokenVariants(quote);

  const queries: { query: string; isInverse: boolean }[] = [];

  // Normal order: BASE/QUOTE (e.g., BTCETH, BTCWETH)
  for (const b of baseVariants) {
    for (const q of quoteVariants) {
      queries.push({ query: `${b}${q}`, isInverse: false });
    }
  }

  // Inverse order: QUOTE/BASE (e.g., ETHBTC, WETHBTC)
  for (const q of quoteVariants) {
    for (const b of baseVariants) {
      queries.push({ query: `${q}${b}`, isInverse: true });
    }
  }

  return queries;
};

interface TradingViewSymbolResult {
  symbol: string;
  description: string;
  type: string;
  exchange: string;
  prefix?: string;
  source_id: string;
  typespecs?: string[];
}

interface TradingViewSearchResponse {
  symbols_remaining: number;
  symbols: TradingViewSymbolResult[];
}

interface UseTradingViewSymbolResult {
  symbol: string | null;
  isLoading: boolean;
  error: string | null;
  fullSymbol: string | null; // PREFIX:SYMBOL format
}

/**
 * Constructs the full TradingView symbol from search result
 * Format: PREFIX:SYMBOL (e.g., MEXC:BTCUSDT)
 */
const getFullSymbol = (result: TradingViewSymbolResult): string => {
  // Remove HTML tags from symbol (API returns <em> tags for highlighting)
  const cleanSymbol = result.symbol.replace(/<\/?em>/g, "");

  if (result.prefix) {
    return `${result.prefix}:${cleanSymbol}`;
  }

  // Some exchanges like CRYPTO don't have prefix
  if (result.source_id) {
    return `${result.source_id}:${cleanSymbol}`;
  }

  return cleanSymbol;
};

/**
 * Map DEX protocol identifiers to TradingView source name patterns
 * Used to match the campaign's selected DEX with TradingView's exchange identifiers
 * Keys are uppercase versions of protocol IDs (e.g., "aerodrome-cl" -> "AERODROME-CL")
 */
const DEX_PROTOCOL_TO_TV_SOURCE: Record<string, string[]> = {
  // PancakeSwap variants (protocol values from @cede/utils)
  "PANCAKE-V3": ["PANCAKESWAP", "PANCAKE"],
  "PANCAKE-V3-BASE": ["PANCAKESWAP", "PANCAKE"],
  "PANCAKE-INFINITY-BSC": ["PANCAKESWAP", "PANCAKE"],
  // Aerodrome
  "AERODROME-CL": ["AERODROME"],
  "AERODROME-CL-2": ["AERODROME"],
  // Raydium (Solana)
  "RAYDIUM": ["RAYDIUM"],
  "RAYDIUM-CLMM": ["RAYDIUM"],
  "RAYDIUM-CPMM": ["RAYDIUM"],
  "RAYDIUM-LAUNCHLAB": ["RAYDIUM"],
  // Orca (Solana)
  "ORCA": ["ORCA"],
  // PumpSwap (Solana)
  "PUMPSWAP": ["PUMPSWAP", "PUMPFUN"],
  // Meteora (Solana)
  "METEORA": ["METEORA"],
  "METEORA-DYN": ["METEORA"],
  "METEORA-DYN2": ["METEORA"],
  "METEORA-DBC": ["METEORA"],
  // Jupiter Studio (Solana)
  "JUPITER-STUDIO": ["JUPITER"],
};

/**
 * Fallback CEX exchanges if no DEX match is available
 */
const FALLBACK_EXCHANGES = ["BINANCE", "COINBASE", "KRAKEN", "BYBIT", "OKX", "BITGET", "KUCOIN", "MEXC"];

/**
 * Scores a symbol result to find the best match
 * Higher score = better match
 * Priority: Preferred DEX (from campaign) > other DEXes > CEX exchanges
 */
const scoreResult = (
  result: TradingViewSymbolResult,
  baseSymbol: string,
  quoteSymbol: string,
  preferredDex?: string,
): number => {
  let score = 0;
  const cleanSymbol = result.symbol.replace(/<\/?em>/g, "").toUpperCase();
  const expectedPair = `${baseSymbol.toUpperCase()}${quoteSymbol.toUpperCase()}`;
  const sourceId = result.source_id?.toUpperCase() || "";

  // Exact match gets highest score
  if (cleanSymbol === expectedPair) {
    score += 100;
  } else if (cleanSymbol.startsWith(expectedPair)) {
    // Starts with expected pair (e.g., BTCUSDT_XXXX for DEX pools)
    score += 80;
  } else {
    // Not a match for our pair
    return 0;
  }

  // Check if this result matches the preferred DEX from the campaign
  const preferredSources = preferredDex ? DEX_PROTOCOL_TO_TV_SOURCE[preferredDex.toUpperCase()] : null;
  const isPreferredDex = preferredSources?.some(
    (pattern) => sourceId === pattern.toUpperCase() || sourceId.includes(pattern.toUpperCase()),
  );

  if (isPreferredDex) {
    // Strongly prefer the campaign's selected DEX
    score += 50;
  }
  // Fallback to well-known CEX exchanges
  else if (FALLBACK_EXCHANGES.some((ex) => sourceId === ex || sourceId.includes(ex))) {
    score += 10;
  }

  // Prefer spot over derivatives
  if (result.type === "spot") {
    score += 20;
  }

  // DEX pools with addresses in symbol get slightly lower priority (but still valid)
  if (cleanSymbol.includes("_")) {
    score -= 5;
  }

  return score;
};

/**
 * Hook to search for TradingView symbols based on token pair
 * @param baseSymbol - Base token symbol (e.g., "BTC")
 * @param quoteSymbol - Quote token symbol (e.g., "USDC")
 * @param preferredDex - Optional DEX protocol to prioritize (e.g., "PANCAKESWAP_V3_BASE", "AERODROME_CL")
 */
export const useTradingViewSymbol = (
  baseSymbol: string | undefined,
  quoteSymbol: string | undefined,
  preferredDex?: string,
): UseTradingViewSymbolResult => {
  const [symbol, setSymbol] = useState<string | null>(null);
  const [fullSymbol, setFullSymbol] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchSymbol = useCallback(async (base: string, quote: string, dexProtocol?: string) => {
    setIsLoading(true);
    setError(null);

    try {
      // Generate all search queries (including ETH/WETH alternatives and inverse pairs)
      const searchQueries = generateSearchQueries(base, quote);

      let bestOverallMatch: { result: TradingViewSymbolResult; score: number; isInverse: boolean } | null = null;

      // Try each search query and find the best match across all
      for (const { query, isInverse } of searchQueries) {
        const url = new URL(TRADINGVIEW_SEARCH_API, window.location.origin);
        url.searchParams.set("text", query);

        const response = await fetch(url.toString());

        if (!response.ok) {
          continue; // Try next query if this one fails
        }

        const data: TradingViewSearchResponse = await response.json();

        if (data.symbols && data.symbols.length > 0) {
          // For scoring, we need to determine which tokens are base/quote in this query
          // For inverse queries, the order is swapped
          const queryBase = isInverse ? quote : base;
          const queryQuote = isInverse ? base : quote;

          // Get variants for proper matching
          const queryBaseVariants = getTokenVariants(queryBase);
          const queryQuoteVariants = getTokenVariants(queryQuote);

          // Score results for this query
          const scoredResults = data.symbols
            .map((result) => {
              // Try all variant combinations for scoring
              let bestScore = 0;
              for (const b of queryBaseVariants) {
                for (const q of queryQuoteVariants) {
                  const score = scoreResult(result, b, q, dexProtocol);
                  if (score > bestScore) {
                    bestScore = score;
                  }
                }
              }
              // Slightly prefer non-inverse matches
              if (isInverse && bestScore > 0) {
                bestScore -= 5;
              }
              return { result, score: bestScore, isInverse };
            })
            .filter((item) => item.score > 0)
            .sort((a, b) => b.score - a.score);

          if (
            scoredResults.length > 0 &&
            (!bestOverallMatch || scoredResults[0]?.score || 0 > bestOverallMatch?.score || 0)
          ) {
            bestOverallMatch = scoredResults[0] || null;
          }
        }

        // If we found an excellent match (score >= 100), stop searching
        if (bestOverallMatch && bestOverallMatch.score >= 100) {
          break;
        }
      }

      if (bestOverallMatch) {
        const cleanSymbol = bestOverallMatch.result.symbol.replace(/<\/?em>/g, "");
        setSymbol(cleanSymbol);
        setFullSymbol(getFullSymbol(bestOverallMatch.result));
      } else {
        setSymbol(null);
        setFullSymbol(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to search symbol");
      setSymbol(null);
      setFullSymbol(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!baseSymbol || !quoteSymbol) {
      setSymbol(null);
      setFullSymbol(null);
      return;
    }

    // Debounce the search
    const timeoutId = setTimeout(() => {
      searchSymbol(baseSymbol, quoteSymbol, preferredDex);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [baseSymbol, quoteSymbol, preferredDex, searchSymbol]);

  return { symbol, fullSymbol, isLoading, error };
};
